import React, { useRef, useEffect } from 'react';

interface InitialChatInputProps {
  text: string;
  setText: (text: string) => void;
  isLoading: boolean;
  onSend: () => void;
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

const InitialChatInput: React.FC<InitialChatInputProps> = ({ text, setText, isLoading, onSend, suggestions, onSuggestionClick }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (text === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // Auto-resize
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  };

  return (
    <div
      className={`relative bg-neutral-10 rounded-2xl border border-neutral-20 transition-all h-[196px] flex flex-col shadow-[0_1px_0_0_#E0E0E0] ${
        isLoading ? 'opacity-70 cursor-not-allowed' : 'focus-within:ring-2 focus-within:ring-[#00A651]/20 focus-within:border-[#00A651]'
      }`}
    >
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        placeholder={isLoading ? 'Freya is thinking...' : 'Start asking here..'}
        className='w-full h-full bg-transparent border-none outline-none resize-none p-20 text-[#000057] placeholder-neutral-60 text-16 disabled:cursor-not-allowed'
      />

      <div className='absolute bottom-12 right-12'>
        <button
          type='button'
          onClick={onSend}
          disabled={!text.trim() || isLoading}
          className='w-36 h-36 rounded-full bg-[#00A651] text-white flex items-center justify-center hover:bg-[#008f45] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md transform active:scale-95 duration-200'
          aria-label='Send message'
        >
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className='absolute top-[9px] left-[10px]'>
            <g filter='url(#filter0_d_37321_57823)'>
              <path
                d='M16.517 7.09153L9.38372 3.52487C4.59205 1.12487 2.62538 3.09153 5.02538 7.8832L5.75038 9.3332C5.95872 9.7582 5.95872 10.2499 5.75038 10.6749L5.02538 12.1165C2.62538 16.9082 4.58372 18.8749 9.38372 16.4749L16.517 12.9082C19.717 11.3082 19.717 8.69153 16.517 7.09153ZM13.8254 10.6249H9.32538C8.98372 10.6249 8.70038 10.3415 8.70038 9.99987C8.70038 9.6582 8.98372 9.37487 9.32538 9.37487H13.8254C14.167 9.37487 14.4504 9.6582 14.4504 9.99987C14.4504 10.3415 14.167 10.6249 13.8254 10.6249Z'
                fill='white'
              />
            </g>
            <defs>
              <filter id='filter0_d_37321_57823' x='-2.54126' y='0' width='28' height='28' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'>
                <feFlood flood-opacity='0' result='BackgroundImageFix' />
                <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
                <feOffset dy='4' />
                <feGaussianBlur stdDeviation='2' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0' />
                <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_37321_57823' />
                <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_37321_57823' result='shape' />
              </filter>
            </defs>
          </svg>
        </button>
      </div>

      {suggestions.length > 0 && !text && (
        <div className='absolute -top-12 left-0 flex flex-wrap gap-2 pointer-events-auto'>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type='button'
              onClick={() => onSuggestionClick(suggestion)}
              className='px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors shadow-sm'
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InitialChatInput;
